import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Col, Empty, Row, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  StopOutlined,
  KeyOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { del, get, put } from '../../../api/client';
import { channelTypeColor, channelTypeLabel } from '../channel-schema';
import type { AdminChannelView } from '../types';
import { ChannelFormModal } from './ChannelFormModal';

/**
 * 支付渠道管理（卡片式）。
 * - GET /admin/payment-channels 脱敏列表（含 hasSecrets / secretKeys，绝不含密钥明文）。
 * - PUT /admin/payment-channels/:id 快速启停（仅提交 isActive，不影响 config/secrets）。
 * - 新建 / 编辑走 ChannelFormModal（密钥只写不回显）。
 */
export function ChannelsPanel() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AdminChannelView | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['payment-channels'],
    queryFn: () => get<AdminChannelView[]>('/admin/payment-channels'),
  });
  const channels = data ?? [];

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['payment-channels'] });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      put(`/admin/payment-channels/${id}`, { isActive: active }),
    onSuccess: () => {
      message.success('渠道状态已更新');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '操作失败'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/admin/payment-channels/${id}`),
    onSuccess: () => {
      message.success('渠道已删除');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  const askDelete = (c: AdminChannelView) => {
    modal.confirm({
      title: '删除支付渠道',
      content: `确认删除「${c.channelName}」？该操作不可撤销，已配置的密钥将一并清除。`,
      okButtonProps: { danger: true },
      okText: '删除',
      cancelText: '取消',
      onOk: () => remove.mutateAsync(c.id),
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Text type="secondary">
          维护支付宝 / 微信 / 银行转账渠道。密钥加密存储、只写不回显；启用后用户下单即可选择该渠道。
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          新建渠道
        </Button>
      </div>

      {isLoading ? (
        <Card loading />
      ) : channels.length === 0 ? (
        <Card>
          <Empty description="暂无支付渠道，点击右上角新建" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {channels.map((c) => {
            const color = channelTypeColor(c.channelType);
            const inactive = !c.isActive;
            return (
              <Col key={c.id} xs={24} sm={12} xl={8}>
                <Card
                  style={{
                    height: '100%',
                    opacity: inactive ? 0.66 : 1,
                    border: c.isActive ? `1px solid ${color}55` : undefined,
                    boxShadow: c.isActive ? `0 6px 18px ${color}14` : undefined,
                  }}
                  styles={{ body: { padding: 20 } }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Space size={8} align="center">
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 12,
                          background: `${color}1a`,
                          color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 18,
                        }}
                      >
                        <SafetyCertificateOutlined />
                      </div>
                      <div>
                        <Typography.Text strong style={{ fontSize: 15 }}>
                          {c.channelName}
                        </Typography.Text>
                        <div>
                          <Tag color={color} style={{ marginTop: 2 }}>
                            {channelTypeLabel(c.channelType)}
                          </Tag>
                        </div>
                      </div>
                    </Space>
                    {c.isActive ? (
                      <Tag icon={<CheckCircleOutlined />} color="success">
                        启用
                      </Tag>
                    ) : (
                      <Tag icon={<StopOutlined />}>停用</Tag>
                    )}
                  </div>

                  <div style={{ margin: '14px 0', minHeight: 44 }}>
                    <Space size={4} wrap>
                      <KeyOutlined style={{ color: c.hasSecrets ? '#0d9488' : '#c0c4cc' }} />
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        密钥：
                      </Typography.Text>
                      {c.hasSecrets && c.secretKeys.length > 0 ? (
                        c.secretKeys.map((k) => (
                          <Tag key={k} color="teal" style={{ fontSize: 11 }}>
                            {k}
                          </Tag>
                        ))
                      ) : (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          未配置
                        </Typography.Text>
                      )}
                    </Space>
                  </div>

                  {c.remark && (
                    <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }} ellipsis={{ rows: 2 }}>
                      {c.remark}
                    </Typography.Paragraph>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: 8,
                      paddingTop: 12,
                      borderTop: '1px dashed #eef0f1',
                    }}
                  >
                    <Space size={4}>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        排序 {c.sortOrder}
                      </Typography.Text>
                      <Tooltip title="编辑">
                        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => setEditing(c)} />
                      </Tooltip>
                      <Tooltip title="删除">
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => askDelete(c)} />
                      </Tooltip>
                    </Space>
                    <Tooltip title={c.isActive ? '点击停用' : '点击启用'}>
                      <Switch
                        size="small"
                        checked={c.isActive}
                        loading={toggle.isPending}
                        onChange={(active) => toggle.mutate({ id: c.id, active })}
                      />
                    </Tooltip>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <ChannelFormModal
        open={creating || editing !== null}
        editing={editing}
        defaultSortOrder={channels.length}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={invalidate}
      />
    </div>
  );
}
